import {
  CheckInOutType,
  OrderItemStatus,
  OrderStatus,
  OrderType,
  Prisma,
  prisma,
} from "client";
import { transformSurveyResult } from "../formTemplates";
import {
  flattenTicketWithBookableItem,
  TicketWithBookableItem,
} from "services/bookableItem/helpers";
import { invalidateCheckinRosterEntry, warmCheckinRosterEntry } from "../checkInOut/warmCheckinCache";

export const getOrderItemByid = async (id: string, selectedDate?: Date) => {
  // Calculate skip based on the pageNumber and pageSize
  try {
    // Reusable slim formResult select: only json is needed from formTemplate for
    // transformSurveyResult; take:1 because only the first result is ever used.
    const formResultSelect = {
      select: {
        id: true,
        surveyResult: true,
        formTemplate: {
          select: { json: true },
        },
      },
    } as const;

    const checksInOutSelect = {
      select: {
        id: true,
        type: true,
        time: true,
        createdBy: {
          select: { id: true, name: true, email: true },
        },
      },
    } as const;

    const include = Prisma.validator<Prisma.OrderItemInclude>()({
      // take:1 — only formResults[0] is ever consumed
      formResults: {
        take: 1,
        include: { formResult: formResultSelect },
      },
      invitationAttendee: {
        select: {
          id: true,
          contact: {
            select: {
              name: true,
              email: true,
              phoneNumber: true,
              company: true,
            },
          },
          invitation: {
            select: { id: true },
          },
        },
      },
      order: {
        select: {
          id: true,
          number: true,
          total: true,
          status: true,
          currency: true,
          tax: true,
          expiryDate: true,
          createdAt: true,
          type: true,
          event: {
            select: { timezone: true, whiteLabelId: true },
          },
          // take:1 — only formResults[0] is ever consumed
          formResults: {
            take: 1,
            include: { formResult: formResultSelect },
          },
          profile: {
            select: {
              id: true,
              name: true,
              email: true,
              phoneNumber: true,
              participantGroups: {
                select: { id: true, translations: true },
              },
              user: {
                select: { image: true },
              },
            },
          },
          invoices: {
            select: { id: true, number: true },
          },
        },
      },
      ticket: {
        select: {
          id: true,
          bookableItem: {
            select: {
              formEntities: {
                select: {
                  // Only json is needed for transform; keep id for reference
                  formTemplate: { select: { id: true, json: true } },
                },
              },
              designs: {
                // id is needed so flattenTicketWithBookableItem can build ticketTemplates
                select: { id: true, translations: true },
              },
              translations: true,
              price: true,
            },
          },
          category: {
            select: { name: true },
          },
        },
      },
      checksInOut: selectedDate?.setHours(0, 0, 0, 0)
        ? {
          where: {
            time: {
              gte: new Date(selectedDate?.setHours(0, 0, 0, 0) || ""),
              lte: new Date(selectedDate?.setHours(23, 59, 59, 999) || ""),
            },
          },
          ...checksInOutSelect,
        }
        : checksInOutSelect,
    });
    const result = await prisma.orderItem.findUnique({
      where: { id },
      include,
    });
    if (!result) return null;
    return {
      ...result,
      invitationAttendee: {
        ...result.invitationAttendee,
      },
      formResult: {
        ...result?.formResults?.[0]?.formResult,
        surveyResult: result?.formResults?.[0]?.formResult?.surveyResult
          ? transformSurveyResult(
            result?.formResults?.[0]?.formResult?.formTemplate?.json,
            result?.formResults?.[0]?.formResult?.surveyResult
          )
          : null,
      },
      ticket: {
        ...result?.ticket,
        ...(result?.ticket
          ? flattenTicketWithBookableItem(
            result?.ticket as unknown as TicketWithBookableItem
          )
          : null),
      },
      invitation: {
        ...result?.invitationAttendee?.invitation,
      },
      order: {
        ...result?.order,
        formResult: {
          ...result?.order?.formResults[0],
          surveyResult: result?.order?.formResults[0]?.formResult?.surveyResult
            ? transformSurveyResult(
              result?.order?.formResults[0]?.formResult?.formTemplate?.json,
              result?.order?.formResults[0]?.formResult?.surveyResult
            )
            : null,
        },
      },
    };
  } catch (error) {
    console.error(error, "error");
    throw error;
  }
};

import { getTicketAvailabilityForApproval } from "../ticketQuantity";

export const updateOrderItem = async (id: string, status: OrderItemStatus) => {
  try {
    const currentItem = await prisma.orderItem.findUnique({
      where: { id },
      select: { status: true, ticketId: true, orderId: true, order: { select: { eventId: true, id: true } } },
    });

    if (!currentItem) {
      throw new Error("Order item not found");
    }

    if (currentItem.status === status) {
      return currentItem;
    }

    if (status === OrderItemStatus.APPROVED) {
      if (currentItem.ticketId && currentItem.orderId) {
        // Count how many items in THIS order are pending for this ticket
        const pendingInOrder = await prisma.orderItem.count({
          where: {
            orderId: currentItem.orderId,
            ticketId: currentItem.ticketId,
            status: OrderItemStatus.PENDING_APPROVAL,
          },
        });

        const { available } = await getTicketAvailabilityForApproval(
          currentItem.ticketId
        );

        // If the order still needs to approve more items than the total available capacity,
        // reject this update so the order doesn't get partially approved.
        if (available < pendingInOrder) {
          throw new Error(
            `Cannot approve order: ${pendingInOrder} tickets remain pending, but only ${available} are available. Please increase the ticket quantity first.`
          );
        }
      }
    }

    const result = await prisma.orderItem.update({
      where: { id },
      data: {
        status,
      },
      include: {
        order: { select: { eventId: true } },
      }
    });

    // If this item is leaving CONFIRMED (cancel, reject, expire, ...) it
    // must NOT be admitted through a stale pre-warmed roster entry. Drop
    // the cached entry so the next scan re-reads from DB and sees the
    // current state. Best-effort: a Redis failure here cannot block the
    // status change.
    if (
      currentItem.status === OrderItemStatus.CONFIRMED &&
      status !== OrderItemStatus.CONFIRMED
    ) {
      invalidateCheckinRosterEntry(id).catch(() => undefined);
    } else if (
      status === OrderItemStatus.CONFIRMED &&
      currentItem.status !== OrderItemStatus.CONFIRMED
    ) {
      warmCheckinRosterEntry(id).catch(() => undefined);
    }

    return result;
  } catch (error) {
    throw error;
  }
};

export const updateOrderItemPrintStatus = async (
  id: string,
  status: boolean
) => {
  try {
    const result = await prisma.orderItem.update({
      where: { id },
      data: {
        printed: status,
        printedAt: new Date(),
      },
    });
    return result;
  } catch (error) {
    return error;
  }
};

export const updateOrderItemsPrintStatus = async (
  ids: string[],
  status: boolean
) => {
  const uniqueIds = [...new Set(ids.filter(Boolean))];
  if (uniqueIds.length === 0) {
    return { count: 0 };
  }

  return prisma.orderItem.updateMany({
    where: { id: { in: uniqueIds } },
    data: {
      printed: status,
      printedAt: new Date(),
    },
  });
};

export const getOrderItems = async (
  orderId: string,
  include?: Prisma.OrderItemInclude,
  select?: Prisma.OrderItemSelect
) => {
  try {
    if (include) {
      return await prisma.orderItem.findMany({
        where: { orderId },
        include,
      });
    }
    return await prisma.orderItem.findMany({
      where: { orderId },
      select: select ?? { id: true },
    });
  } catch (error) {
    throw error;
  }
};

export const getUserOrderItems = async (
  userId: string,
  orderType: OrderType = OrderType.TICKET,
  pastOrComing?: string,
  whiteLabelId?: string
) => {
  try {
    const orders = await prisma.order.findMany({
      where: {
        type: orderType,
        event: {
          ...(whiteLabelId && { whiteLabelId }),
          ...(pastOrComing && {
            startDate: {
              [pastOrComing === "past" ? "lte" : "gte"]: new Date(),
            },
          }),
        },
        profile: {
          userId,
        },
      },
      select: { id: true },
    });
    const orderIds = orders.map((order) => order.id);
    if (orderIds.length === 0) return [];

    const orderItems = await prisma.orderItem.findMany({
      where: {
        orderId: { in: orderIds },
      },
      include: {
        order: {
          include: {
            profile: true,
          },
        },
        ticket: {
          include: {
            event: {
              include: {
                venues: true,
                translations: true,
              },
            },
          },
        },
        invitationAttendee: true,
      },
    });

    return orderItems;
  } catch (error: any) {
    throw new Error(`Failed to get tickets by user: ${error.message}`);
  }
};

export const getOrderItemsByEventId = async (eventId: string) => {
  try {
    const completedOrders = await prisma.order.findMany({
      where: { eventId, status: OrderStatus.COMPLETED },
      select: { id: true },
    });
    const completedOrderIds = completedOrders.map((order) => order.id);
    if (completedOrderIds.length === 0) return [];

    const orderItems = await prisma.orderItem.findMany({
      where: {
        orderId: { in: completedOrderIds },
        status: OrderItemStatus.CONFIRMED,
        checksInOut: {
          some: {
            type: CheckInOutType.CHECK_IN,
          },
        },
      },
      include: {
        ticket: true,
        formResults: {
          include: {
            formResult: true,
          },
        },
        order: {
          include: {
            profile: true,
            formResults: {
              include: {
                formResult: true,
              },
            },
          },
        },
      },
    });
    return orderItems;
  } catch (error) {
    throw error;
  }
};
